import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import BaseModal from '../../calendar/modals/BaseModal';
import HeaderText from '../../../shared/components/HeaderText/HeaderText';
import Text from '../../../shared/components/Text/Text';
import { clearLastImportedProgramId } from '../ducks/actions';

// One-time "Import complete!" confirmation shown on the Build page after the
// AI import wizard's build-landing path (LD flag `aiImportHomeLanding` OFF)
// routes here. The wizard stashes `lastImportedProgramId` and navigates to
// /build; this modal is the explicit feedback that replaces the old
// fire-and-forget toast. Dismissed by the coach via OK (or overlay/Esc),
// which clears the transient signal. The id-match guard mirrors the
// Navigator banner's so the modal only fires for the program that was just
// imported and selected — never for a stale id from another surface.
const ModalBody = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-family: 'Nunito Sans', sans-serif;
`;

const Title = styled(HeaderText)`
  margin: 0 0 12px;
  font-size: 22px;
  font-weight: 800;
  color: #14162B;
`;

const Body = styled(Text)`
  margin: 0 0 28px;
  font-size: 15px;
  line-height: 1.5;
  max-width: 340px;
`;

const OkButton = styled('button')`
  padding: 8px 28px;
  min-width: 120px;
  background: #14162B;
  color: #FFFFFF;
  border: none;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1.5;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    background: #000014;
    box-shadow: 0 6px 18px rgba(20, 22, 43, 0.28);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(20, 22, 43, 0.2);
  }
`;

const AIImportSuccessModal = () => {
  const dispatch = useDispatch();
  const lastImportedProgramId = useSelector(
    (state) => state.aiProgramImport.lastImportedProgramId,
  );
  const selectedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );

  const isOpen = Boolean(lastImportedProgramId)
    && String(lastImportedProgramId) === String(selectedProgram?.id);

  const handleClose = useCallback(() => {
    dispatch(clearLastImportedProgramId());
  }, [dispatch]);

  if (!isOpen) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      width='440px'
      minWidth='320px'
      maxWidth='440px'
      padding='32px'
    >
      <ModalBody>
        <Title as='h2'>Import complete!</Title>
        <Body>
          Here&apos;s your imported program. Make changes or load it into a
          calendar to schedule.
        </Body>
        <OkButton type='button' onClick={handleClose}>
          OK
        </OkButton>
      </ModalBody>
    </BaseModal>
  );
};

export default AIImportSuccessModal;
